import { ErrorAlert } from '@/components/_ui/ErrorAlert'
import LandingPageLayout from '@/components/Layouts/LandingPageLayout'
import { ProductDetails } from '@/components/ProductDetails'
import { useGTM } from '@/lib/hooks/google-analytics/useGTM'
import { useInitTranslations } from '@/lib/hooks/initializers/useInitTranslations'
import { getProductPageStaticPaths } from '@/lib/staticPaths/getProductPageStaticPaths'
import { isAvailableForCustomerType } from '@/lib/utils/user/isAvailableForCustomerType'
import useGlobalStore from '@/store/useGlobalStore'
import type { GetStaticPaths, InferGetStaticPropsType } from 'next'
import Head from 'next/head'
import { useSendEventOnLoad } from '@/lib/hooks/helpers/useSendEventOnLoad'
import { Error404 } from '@/components/_ui/Error404'
import { getStaticProps } from './server'

export const getStaticPaths = (async () => ({
  paths: await getProductPageStaticPaths(),
  fallback: 'blocking',
})) satisfies GetStaticPaths

export { getStaticProps }

const ProductsPage = ({
  productData,
  translations,
  title,
  metaData,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  useInitTranslations({ translations })

  const {
    user: { customerType },
  } = useGlobalStore()
  const { gtm } = useGTM()

  useSendEventOnLoad({
    event: () => gtm.viewItem({ product: productData }),
  })

  const handleDisplayProductPage = () => {
    if (!productData) {
      return (
        <Error404
          errorLabel="404_error_product_label"
          errorDescription="404_error_product_description"
        />
      )
    }

    if (
      isAvailableForCustomerType({
        customerType,
        availableFor: productData?.availableFor,
      }) === false
    ) {
      return (
        <ErrorAlert
          error={{
            error: {
              message: 'Product unavailable',
              code: 401,
            },
          }}
        />
      )
    }

    return <ProductDetails productData={productData} />
  }

  return (
    <LandingPageLayout>
      <Head>
        <title>{title}</title>
        <meta name="twitter:card" content="summary_large_image" />
        {metaData &&
          metaData.map((metaElement) => (
            <meta
              property={metaElement.property}
              content={metaElement.content?.toString() || undefined}
              key={metaElement.key}
            />
          ))}
      </Head>

      <main
        className={`color flex min-h-screen flex-col items-center justify-center overflow-x-clip px-8 pb-0 md:pt-2 lg:pt-20`}
      >
        {handleDisplayProductPage()}
      </main>
    </LandingPageLayout>
  )
}

export default ProductsPage
